// src/components/molecules/DateRangePicker/DateRangePicker.jsx
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Divider,
  Grid,
  useTheme
} from '@mui/material';
import {
  Event as EventIcon,
  Create as CreateIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ptBR } from 'date-fns/locale';
import DatePickerCalendarHeader from '../DatePickerCalendarHeader/DatePickerCalendarHeader';

/**
 * DateRangePicker - Componente molecular para seleção de intervalo de datas
 *
 * Usa @mui/x-date-pickers com tema do sistema (dark/light).
 */
const DateRangePicker = ({
  value,
  onChange,
  onClose,
  label = 'Selecionar Período',
  size = 'small'
}) => {
  const theme = useTheme();

  // Helper: converte Date UTC-midnight ou string YYYY-MM-DD → objeto Date local correto
  const parseInitialDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    if (isNaN(d)) return null;
    // Se for UTC midnight (hora 0 em UTC), interpretar como data local neutra
    // Criar uma nova data usando componentes UTC para evitar deslocamento de fuso
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  };

  const [startDate, setStartDate] = useState(parseInitialDate(value?.startDate));
  const [endDate, setEndDate] = useState(parseInitialDate(value?.endDate));
  const [dateType, setDateType] = useState(value?.dateType || 'published');

  // Calcular duração do período
  const periodDuration = useMemo(() => {
    if (!startDate || !endDate) return null;
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    if (diffDays === 1) return '1 dia';
    if (diffDays < 7) return `${diffDays} dias`;
    if (diffDays === 7) return '1 semana';
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} semanas`;
    if (diffDays < 60) return '1 mês';
    return `${Math.floor(diffDays / 30)} meses`;
  }, [startDate, endDate]);

  const isEndBeforeStart = startDate && endDate && endDate < startDate;

  const handleApply = () => {
    if (startDate && endDate && !isEndBeforeStart) {
      // Manter a data civil em meia-noite local para não deslocar o dia pelo fuso.
      const toLocalMidnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
      onChange({
        startDate: toLocalMidnight(startDate),
        endDate: toLocalMidnight(endDate),
        dateType: dateType
      });
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // Estilo customizado para os DatePickers
  const pickerSx = {
    width: '100%',
    '& .MuiInputBase-root': {
      fontSize: '0.875rem',
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Dialog open={true} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon color="primary" />
            {label}
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Toggle para tipo de data */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
                Filtrar por:
              </Typography>
              <ToggleButtonGroup
                value={dateType}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) setDateType(newValue);
                }}
                fullWidth
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    py: 1,
                    textTransform: 'none',
                    fontSize: '0.875rem'
                  }
                }}
              >
                <ToggleButton value="published">
                  <EventIcon sx={{ mr: 1, fontSize: 18 }} />
                  Data de Publicação
                </ToggleButton>
                <ToggleButton value="created">
                  <CreateIcon sx={{ mr: 1, fontSize: 18 }} />
                  Data de Cadastro
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* DatePickers lado a lado */}
            <Box sx={{ mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <DatePicker
                    label="Data Inicial"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    maxDate={endDate || undefined}
                    views={['year', 'month', 'day']}
                    openTo="day"
                    yearsPerRow={4}
                    monthsPerRow={3}
                    slots={{ calendarHeader: DatePickerCalendarHeader }}
                    slotProps={{
                      textField: {
                        size: size,
                        fullWidth: true,
                        sx: pickerSx
                      },
                      day: {
                        sx: {
                          '&.Mui-selected': {
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            '&:hover': {
                              backgroundColor: theme.palette.primary.dark,
                            }
                          }
                        }
                      }
                    }}
                    format="dd/MM/yyyy"
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="Data Final"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    minDate={startDate || undefined}
                    views={['year', 'month', 'day']}
                    openTo="day"
                    yearsPerRow={4}
                    monthsPerRow={3}
                    slots={{ calendarHeader: DatePickerCalendarHeader }}
                    slotProps={{
                      textField: {
                        size: size,
                        fullWidth: true,
                        error: isEndBeforeStart,
                        helperText: isEndBeforeStart
                          ? 'Data final não pode ser anterior à inicial'
                          : '',
                        sx: pickerSx
                      },
                      day: {
                        sx: {
                          '&.Mui-selected': {
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            '&:hover': {
                              backgroundColor: theme.palette.primary.dark,
                            }
                          }
                        }
                      }
                    }}
                    format="dd/MM/yyyy"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Mostrar duração do período */}
            {periodDuration && !isEndBeforeStart && (
              <Box sx={{
                mt: 2,
                p: 2,
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(144, 202, 249, 0.12)'
                  : 'rgba(25, 118, 210, 0.08)',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <AccessTimeIcon fontSize="small" color="primary" />
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                  Período: {periodDuration}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancel} color="inherit" sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            color="primary"
            disabled={!startDate || !endDate || isEndBeforeStart}
            sx={{ textTransform: 'none', minWidth: 100 }}
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

DateRangePicker.propTypes = {
  value: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    dateType: PropTypes.oneOf(['published', 'created'])
  }),
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  label: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium'])
};

export default DateRangePicker;
